import * as React from 'react';

import type { BillableWorkRM } from 'ab-requestModels/reportType/reportType.requestModel';

import type { FieldItem } from './BillableWorkField';
import BillableWorkField from './BillableWorkField';
import styles from './styles.module.scss';
import type { ReportBlockFieldFormModel } from '../../../../../Shared/formModel';
import { useReportTypePreviewHighlightContext } from '../../../../Context/ReportTypePreviewHighlight';

interface OwnProps {
	billableWork: BillableWorkRM;
	reportFieldsByIdMap: Record<string, ReportBlockFieldFormModel>;
}

type Props = OwnProps;

const BillableWorkFieldList: React.FC<Props> = (props) => {
	const { billableWork, reportFieldsByIdMap } = props;

	const { highlight, focusedVirtualId, virtualIds } = useReportTypePreviewHighlightContext();

	const leftFields = React.useMemo(() => {
		const { workTypeReportBlockFieldVirtualId, definitionFields } = billableWork;

		const fieldList: FieldItem[] = [];
		fieldList.push({
			id: workTypeReportBlockFieldVirtualId,
			name: `Type: ${reportFieldsByIdMap[workTypeReportBlockFieldVirtualId]?.name}`,
		});
		fieldList.push(...definitionFields.map((_field) => ({
			id: _field.reportBlockFieldVirtualId,
			name: `Definition: ${reportFieldsByIdMap[_field.reportBlockFieldVirtualId].name}`,
		})));
		return fieldList;
	}, [billableWork, reportFieldsByIdMap]);

	const rightFields = React.useMemo(() => {
		const { workQuantityReportBlockFieldVirtualId, informationFields } = billableWork;

		const fieldList: FieldItem[] = [];
		if (workQuantityReportBlockFieldVirtualId) {
			fieldList.push({
				id: workQuantityReportBlockFieldVirtualId,
				name: `Quantity: ${reportFieldsByIdMap[workQuantityReportBlockFieldVirtualId]?.name}`,
			});
		}
		fieldList.push(...informationFields.map((_field) => ({
			id: _field.reportBlockFieldVirtualId,
			name: `Information: ${reportFieldsByIdMap[_field.reportBlockFieldVirtualId].name}`,
		})));
		return fieldList;
	}, [billableWork, reportFieldsByIdMap]);

	const handleFieldFocus = React.useCallback((id: string) => {
		highlight([...leftFields, ...rightFields].map((_field) => _field.id), id);
	}, [highlight, leftFields, rightFields]);

	const renderField = React.useCallback((field: FieldItem) => {
		return (
			<BillableWorkField
				field={field}
				isFocused={field.id === focusedVirtualId}
				isHighlighted={virtualIds[field.id]}
				key={field.id}
				onFocus={handleFieldFocus}
			/>
		);
	}, [focusedVirtualId, handleFieldFocus, virtualIds]);

	return (
		<div className={styles.BillableWorkFieldList}>
			<div className={styles.list}>
				{leftFields.map(renderField)}
			</div>
			<div className={styles.list}>
				{rightFields.map(renderField)}
			</div>
		</div>
	);
};

export default React.memo(BillableWorkFieldList);
